import Position from '@advisor/models/store/investmentModel/Position';
import {TAX_SETTINGS} from '@advisor/util/constants';

export default class ModelPortfolio {
  constructor(riaId = null) {
    this.assetManagerId = null;
    this.assetManagerName = 'Custom';
    this.description = null;
    this.modelId = null;
    this.modelName = null;
    this.modelPortfolioDetails = [];
    this.riaId = riaId;
    this.taaId = null;
    this.taaName = null;
    this.taaOnOff = 0;
    this.lastUpdate = null;
    this.status = 0;
    this.listUniqueKey = 0;
    this.newUniqueKey = -1;
  }

  static make(objMix, isCompositionWeightCalculated = false) {
    const objModelPortfolio = new ModelPortfolio();
    const mixModelPortfolio = Object.assign(objModelPortfolio, objMix);

    objModelPortfolio.modelPortfolioDetails = [];

    if (!(objMix.modelPortfolioDetails && objMix.modelPortfolioDetails.length > 0)) {
      return mixModelPortfolio;
    }

    const withComposites = objMix.modelPortfolioDetails.filter((row) => row.compositeId !== 0);
    const withNoComposites = objMix.modelPortfolioDetails.filter((row) => row.compositeId === 0);

    withComposites.forEach((objTicker) =>
      objModelPortfolio.addTicker({
        ...objTicker,
        isCompositionWeightCalculated,
      })
    );

    withNoComposites.forEach((objTicker) => objModelPortfolio.addTicker(objTicker));

    return mixModelPortfolio;
  }

  addNewPosition(objPosition) {
    // eslint-disable-next-line no-param-reassign
    objPosition.uniqueKey = this.newUniqueKey;
    const tempModelPortfolioDetails = [...this.modelPortfolioDetails];

    tempModelPortfolioDetails.unshift(objPosition);
    this.modelPortfolioDetails = tempModelPortfolioDetails;
    this.newUniqueKey -= 1;
  }

  addTicker(objTicker) {
    // eslint-disable-next-line no-param-reassign
    objTicker.uniqueKey = this.listUniqueKey;
    const objNewPosition = Position.make(objTicker);
    const tempModelPortfolioDetails = [...this.modelPortfolioDetails];

    this.listUniqueKey += 1;
    tempModelPortfolioDetails.push(objNewPosition);
    this.modelPortfolioDetails = tempModelPortfolioDetails;
  }

  removeTicker(index) {
    const tempModelPortfolioDetails = [...this.modelPortfolioDetails];

    tempModelPortfolioDetails.splice(index, 1);
    this.modelPortfolioDetails = tempModelPortfolioDetails;
  }

  updateTicker(objTicker, index) {
    const newPositon = Position.make(objTicker);

    if (newPositon.isNew) {
      newPositon.uniqueKey = this.newUniqueKey;
      this.newUniqueKey -= 1;
    } else {
      newPositon.uniqueKey = this.listUniqueKey;
      this.listUniqueKey += 1;
    }

    const tempModelPortfolioDetails = [...this.modelPortfolioDetails];

    if (newPositon.isComposite()) {
      newPositon.getCompositeExpense();
      newPositon.getCompositeTaxSetting();
    }

    tempModelPortfolioDetails.splice(index, 1, newPositon);
    this.modelPortfolioDetails = tempModelPortfolioDetails;
  }

  /**
   * This returns summation of taxable tickers to show at label
   * @returns {Number} acc
   */
  getTaxables() {
    const totalTaxable = this.modelPortfolioDetails.reduce((acc, ticker) => {
      let value = acc;

      if (TAX_SETTINGS.TAXABLES.includes(ticker.tickerTax)) {
        value += parseFloat(ticker.weight);
      }

      return value;
    }, 0);

    return Number(totalTaxable.toFixed(2));
  }

  /**
   * This returns summation of non taxable tickers
   * @returns {Number}
   */
  getNonTaxables() {
    const totalNonTaxable = this.modelPortfolioDetails.reduce((acc, ticker) => {
      let value = acc;

      if (TAX_SETTINGS.NON_TAXABLES.includes(ticker.tickerTax)) {
        value += parseFloat(ticker.weight);
      }

      return value;
    }, 0);

    return Number(totalNonTaxable.toFixed(2));
  }

  removeNewTickers() {
    this.modelPortfolioDetails = this.modelPortfolioDetails.filter((objPosition) => objPosition.isNew === false);
  }

  validTickers() {
    return !this.modelPortfolioDetails.some((item) => item.isNewPosition());
  }

  /**
   * InvestmentModel Rules: ModelPortfolios
   * @returns {Boolean}
   */
  validate() {
    const taxed = this.getTaxables();
    const nonTaxed = this.getNonTaxables();

    if (this.modelPortfolioDetails.length <= 0) {
      return {status: 'error', message: `Error: Please add some positions.`};
    }

    // Rule: All tickers should be valid
    if (!this.validTickers()) {
      return {
        status: 'error',
        message: `Error: Please select a value for all the new tickers of this Model Portfolio before saving.`,
      };
    }

    // Rule: The sum of all taxed and non-taxed values should be 100%
    if (taxed !== 100 || nonTaxed !== 100) {
      return {
        status: 'error',
        message: `Error: Taxable and tax-advantaged positions should be a 100% each. Please update accordingly your Tax Settings and Weights for this Model Portfolio before saving.`,
      };
    }

    // Data is verified and Ok:
    return {status: 'success', message: null};
  }

  /**
   * Doubt Check about adding new two ticker
   * @param {Position} newTicker
   *
   * @returns {Position|Boolean}
   */
  getDuplicateTicker(newTicker) {
    const arrDuplicateTickers = this.modelPortfolioDetails.filter(
      (ticker) =>
        ticker.tickerId !== -1 && ticker.uniqueKey !== newTicker.uniqueKey && ticker.tickerName === newTicker.tickerName
    );

    return arrDuplicateTickers.length > 0 ? arrDuplicateTickers.pop() : false;
  }

  static processDuplicateTickerTaxSettings(newTicker, duplicateTicker) {
    if (duplicateTicker.tickerTax === TAX_SETTINGS.STATUSES.Taxable) {
      // eslint-disable-next-line no-param-reassign
      newTicker.tickerTax = TAX_SETTINGS.STATUSES.TaxAdvantaged;
    } else if (duplicateTicker.tickerTax === TAX_SETTINGS.STATUSES.TaxAdvantaged) {
      // eslint-disable-next-line no-param-reassign
      newTicker.tickerTax = TAX_SETTINGS.STATUSES.Taxable;
    }

    return newTicker;
  }
}
